import { InfoButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import { createBackgroundProcess } from '../../utils/BackgroundJobs';
import SalesAllocationAutoAllocateMutation from '../../graphql/mutations/salesLedger/SalesAllocationAutoAllocateMutation.graphql';
import SalesAllocationReverseAllocationsMutation from '../../graphql/mutations/salesLedger/SalesAllocationReverseAllocationsMutation.graphql';
import SalesAllocationPostMutation from '../../graphql/mutations/salesLedger/SalesAllocationPostMutation.graphql';
import updateStoreFieldValues from '../../components/connectedForm/utils/updateStoreFieldValues';
import { PostSelectButton } from '../../components/SelectButton';
import getClientMutationId from '../../utils/getClientMutationId';
import { DocumentStatus, ISalesAllocation } from '../../graphql/schema/p4types';
import { IDeprecatedWorkspaceAction, IWorkspaceAction } from '../../@types/workspaceTypes';
import { SalesAllocationAction } from '../../graphql/entities/salesLedger/SalesAllocationAuditReport';
import React, { useMemo } from 'react';

const canBePosted: (allocation: ISalesAllocation) => boolean = (allocation) => {
  if (allocation && allocation.allocationOnly === false) return false;
  if (allocation.customer === null) return false;
  if (allocation && allocation.documentStatus) {
    return allocation.documentStatus === DocumentStatus.Draft;
  }
  return false;
};

const canBeReversed: (allocation: ISalesAllocation) => boolean = (allocation) => {
  return (
    !!(allocation && allocation.documentStatus && allocation.documentStatus === DocumentStatus.Posted) &&
    !allocation.foreignCurrency
  );
};

const autoAllocate: (props: any) => void = (props) => {
  const { values, connectedForm } = props;

  if (hasValidationErrors(connectedForm)) return;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };

  createBackgroundProcess(
    SalesAllocationAutoAllocateMutation,
    input,
    props,
    null,
    'Auto Allocate',
    updateStoreFieldValues
  );
};

const reverseAllocations: (props: any) => void = (props) => {
  const { values } = props;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(
    SalesAllocationReverseAllocationsMutation,
    input,
    props,
    '/Customers/Allocation/Workspace',
    'Reverse Allocations',
    null
  );
};

const postAllocate: (props: any) => void = (props) => {
  const { values, connectedForm } = props;
  if (hasValidationErrors(connectedForm)) return;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(SalesAllocationPostMutation, input, props, 'back', 'Post Allocations', null);
};

export const formActionsForPermissions = [
  { description: 'Auto Allocate' },
  { description: 'Post Allocations' },
  { description: 'Reverse Allocations' },
  { actionReference: SalesAllocationAction.AllocationAuditReport },
];

export const useSalesAllocationActions = () => {
  return useMemo(() => {
    //Look in company.actions table, figure out what the action is called, probably based on the description.
    //Call the new actionReference the same thing. (Or migrate)
    const actions: Array<IWorkspaceAction | IDeprecatedWorkspaceAction> = [
      {
        key: 'SALES_LEDGER_ALLOCATIONS_AUTO ALLOCATE',
        actionReference: 'SALES_LEDGER_ALLOCATIONS_AUTO ALLOCATE',
        description: 'Auto Allocate',
        screenReference: 'SALES_LEDGER_ALLOCATIONS_AUTO ALLOCATE',
        component: InfoButton,
        fn: autoAllocate,
        individualButton: true,
        performSave: true,
        saveMessage: 'Transactions auto allocated',
        condition: canBePosted,
      },
      {
        key: 'SALES_LEDGER_ALLOCATIONS_POST ALLOCATIONS',
        actionReference: 'SALES_LEDGER_ALLOCATIONS_POST ALLOCATIONS',
        description: 'Post Allocations',
        screenReference: 'SALES_LEDGER_ALLOCATIONS_POST ALLOCATIONS',
        component: (props: any) => <PostSelectButton {...props} temporaryDisableDelayOnClick={2000} />,
        fn: postAllocate,
        individualButton: true,
        performSave: true,
        saveMessage: 'Transactions allocated',
        condition: canBePosted,
      },
      {
        key: 'SALES_LEDGER_ALLOCATIONS_REVERSE ALLOCATIONS',
        actionReference: 'SALES_LEDGER_ALLOCATIONS_REVERSE ALLOCATIONS',
        description: 'Reverse Allocations',
        screenReference: 'SALES_LEDGER_ALLOCATIONS_REVERSE ALLOCATIONS',
        component: InfoButton,
        fn: reverseAllocations,
        individualButton: true,
        performSave: true,
        saveMessage: 'Allocations reversed',
        condition: canBeReversed,
      },
    ];

    return [actions];
  }, []);
};
